import { createI18n } from 'vue-i18n';
import en from './messages/en';
import { DEFAULT_LOCALE, readStoredLocale, storeLocale, type Locale } from './locale';

/*
 * One app, three languages. docs/ROADMAP.md's Phase 9 carries the decisions; this is the wiring.
 *
 * **Components import `t` from here rather than calling `useI18n()`.** The composer's `t` reads
 * the locale ref, so a template that calls it re-renders on a language change exactly as a
 * `useI18n()` binding would — and doing it this way means the plain modules under `src/api/`
 * (which return sentences and have no component to sit in) use the identical import, and no test
 * has to install a plugin to mount anything.
 *
 * English ships with the bundle because it is the fallback for every unmatched language;
 * Afrikaans and Spanish are separate chunks, since two unused languages are a download somebody
 * pays for on a phone.
 */

export type MessageSchema = typeof en;

const loaders: Record<Locale, () => Promise<{ default: MessageSchema }>> = {
  en: () => Promise.resolve({ default: en }),
  af: () => import('./messages/af'),
  es: () => import('./messages/es'),
};

export const i18n = createI18n<{ message: MessageSchema }, Locale, false>({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  // Only English is here; the other two are set as they load. The cast is the price of that,
  // and it is one line rather than two empty message trees pretending to be translations.
  messages: { en } as unknown as Record<Locale, MessageSchema>,
});

/** Bound by vue-i18n, so it can be imported and called anywhere — component or plain module. */
export const { t } = i18n.global;

/**
 * Switches language, loading its messages if this is the first time they are needed.
 *
 * `remember: false` is what detection uses. A stored locale means "this person chose this", so
 * writing one from a guess would mean never being able to detect for them again.
 *
 * Rejects if the chunk cannot be fetched, leaving the app in the language it already had —
 * the caller says so, rather than the page half-changing.
 */
export async function setLocale(
  locale: Locale,
  { remember = true }: { remember?: boolean } = {},
): Promise<void> {
  if (!i18n.global.availableLocales.includes(locale)) {
    const messages = await loaders[locale]();
    i18n.global.setLocaleMessage(locale, messages.default);
  }

  i18n.global.locale.value = locale;
  // Assistive technology reads this to choose a voice, and it is the one signal about the page's
  // language that is not itself a translated string.
  document.documentElement.lang = locale;

  if (remember) storeLocale(locale);
}

/**
 * The language to open a visit in, applied before the app mounts.
 *
 * A remembered choice wins; otherwise **English**, and nothing is stored — English here is a
 * default, not a choice, which is what leaves it overridable. A choice on the subject overrides
 * it when `/me` lands a moment later, and that is the store's job: it is what carries somebody's
 * choice from their phone to their laptop.
 *
 * The browser's own languages are deliberately not consulted. They used to be, and it made the
 * first screen unpredictable: a phone set to Afrikaans or Spanish opened the app in a language
 * nobody had asked for. The language now only ever changes because somebody changed it.
 */
export async function bootLocale(): Promise<void> {
  const chosen = readStoredLocale();

  await setLocale(chosen ?? DEFAULT_LOCALE, { remember: chosen !== null });
}
